// @flow

import React, { useEffect, useRef } from 'react';
import type { Element } from 'react';

import { connect } from 'react-redux';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import {
  compact, flow, isEmpty, isNil, omitBy, values as getValues
} from 'lodash';

import { withTranslation } from '@i18n';

import type { GlobalState } from '@globalTypes';
import type { Props, State } from '../types';

import { setFilterValues } from '../redux/actions';

import {
  StyledCover,
  StyledHeader,
  StyledBackButton,
  StyledTitle,
  StyledClearAll,
  StyledBody,
  StyledFooter,
  StyledResultsButton
} from './styles';

const Wrapper = ({
  children,
  values,
  mobileFilters,
  t,
  onChange,
  onClose,
  setFilterValues
}: Props): Element<any> => {
  const { current: initialValues }: { current: { [string]: any } } = useRef({ ...values });

  const { values: reduxValues, currentFilter }: State = mobileFilters;
  const hasFilterOpen: boolean = currentFilter !== null;
  const hasFilters: boolean = !flow(
    getValues,
    compact,
    isEmpty
  )(reduxValues);

  const handleClose = (): void => {
    setFilterValues(initialValues);
    setTimeout(onClose);
  };

  const handleShow = (): void => {
    onChange(omitBy(reduxValues, isNil));
    onClose();
  };

  const clearValues = (): void => {
    setFilterValues({});
    onChange({});
  };

  const onUnmount = (): void => {
    setTimeout(() => setFilterValues({}));
  };

  useEffect((): Function => {
    setFilterValues(values);

    return onUnmount;
  }, []);

  React.useEffect((): Function => {
    (document.body: any).setAttribute('style', 'position: fixed; width: 100vw;');

    return (): void => {
      (document.body: any).setAttribute('style', '');
    };
  }, []);

  return (
    <StyledCover>
      <StyledHeader>
        <StyledBackButton icon={faChevronLeft} onClick={handleClose} />

        <StyledTitle>{t('MobileFilters.Title')}</StyledTitle>

        {hasFilters && (
          <StyledClearAll onClick={clearValues}>{t('MobileFilters.ClearAll')}</StyledClearAll>
        )}
      </StyledHeader>

      <StyledBody>{children}</StyledBody>

      {hasFilters && !hasFilterOpen && (
        <StyledFooter>
          <StyledResultsButton onClick={handleShow} label={t('MobileFilters.ShowResults')} />
        </StyledFooter>
      )}
    </StyledCover>
  );
};

const mapStateToProps = ({ mobileFilters }: GlobalState) => ({
  mobileFilters
});

const mapDispatchToProps = { setFilterValues };

export default flow(
  withTranslation('common'),
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(Wrapper);
